import { NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  CommissionTierRule,
  INSURANCE_COMMISSION_BASIS_OPTIONS,
  commissionBasisLabelKey,
  defaultCommissionRulesJson,
  getCommissionPlanRulesFields,
  parseCommissionTiers,
  serializeCommissionTiers
} from './insurance-commission-plan-rules-schemas';
import {
  CreateInsuranceCommissionPlanPayload,
  InsuranceApiService,
  InsuranceCommissionPlanDetail,
  UpdateInsuranceCommissionPlanPayload
} from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-commission-plan-detail',
  imports: [
    NgTemplateOutlet,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    StructuredJsonFormComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    SelectModule,
    ButtonModule,
    DialogModule,
    TableModule,
    PartySelectorComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.commissions.planDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.commissions.plans.help.detail.title'"
        [descriptionKey]="'insurance.commissions.plans.help.detail.description'"
        [bulletKeys]="[
          'insurance.commissions.plans.help.detail.bullets.basis',
          'insurance.commissions.plans.help.detail.bullets.clawback',
          'insurance.commissions.plans.help.detail.bullets.rules'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/commission-plans']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.commissions.planDetail.createTitle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.commissions.plans.help.planEditor.title'"
              [descriptionKey]="'insurance.commissions.plans.help.planEditor.description'"
              [bulletKeys]="[
                'insurance.commissions.plans.help.planEditor.bullets.legalEntity',
                'insurance.commissions.plans.help.planEditor.bullets.basis',
                'insurance.commissions.plans.help.planEditor.bullets.active'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label><span>{{ t('insurance.commissions.planDetail.fields.legalEntity') }}</span><ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" /></label>
            <label><span>{{ t('insurance.commissions.planDetail.fields.code') }}</span><input pInputText [(ngModel)]="createForm.code" name="code" /></label>
            <label><span>{{ t('insurance.commissions.planDetail.fields.name') }}</span><input pInputText [(ngModel)]="createForm.name" name="name" /></label>
            <label>
              <span>{{ t('insurance.commissions.planDetail.fields.basis') }}</span>
              <p-select
                [options]="basisOptions"
                optionValue="value"
                [(ngModel)]="createForm.basis"
                name="basis"
                appendTo="body"
                (ngModelChange)="onCreateBasisChanged($event)">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label><span>{{ t('insurance.commissions.planDetail.fields.clawbackMonths') }}</span><p-inputNumber [(ngModel)]="createForm.clawbackMonths" name="clawbackMonths" [min]="0" [max]="120" /></label>
            <label class="checkbox"><p-checkbox binary="true" [(ngModel)]="createForm.isActive" name="isActive" /><span>{{ t('insurance.commissions.planDetail.fields.isActive') }}</span></label>
            <div class="wide rules-block">
              <span class="field-label">{{ t('insurance.commissions.planDetail.fields.rules') }}</span>
              @if (createForm.basis === 'Tiered') {
                <ng-container [ngTemplateOutlet]="tierRulesEditor" [ngTemplateOutletContext]="{ mode: 'create' }" />
              } @else {
                <ain-structured-json-form [fields]="createRulesFields()" [(payloadJson)]="createForm.rulesJson" />
              }
            </div>
          </form>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      } @else if (!plan()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/commission-plans']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEdit()"></button>
        </div>

        <section class="card header">
          <h3>{{ plan()!.code }} - {{ plan()!.name }}</h3>
          <div class="meta">
            <span>{{ t(basisLabelKey(plan()!.basis)) }}</span>
            <span>{{ t('insurance.commissions.planDetail.fields.clawbackMonths') }}: {{ plan()!.clawbackMonths ?? '-' }}</span>
            <strong>{{ plan()!.isActive ? t('common.enabled') : t('common.disabled') }}</strong>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.commissions.planDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'rules'" (click)="activeTab.set('rules')">{{ t('insurance.commissions.planDetail.tabs.rules') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="section-title">
              <h4>{{ t('insurance.commissions.planDetail.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.commissions.plans.help.summary.title'"
                [descriptionKey]="'insurance.commissions.plans.help.summary.description'"
                [bulletKeys]="[
                  'insurance.commissions.plans.help.summary.bullets.name',
                  'insurance.commissions.plans.help.summary.bullets.basis',
                  'insurance.commissions.plans.help.summary.bullets.clawback'
                ]" />
            </div>
            <form class="grid" (ngSubmit)="saveEdit()">
              <label><span>{{ t('insurance.commissions.planDetail.fields.name') }}</span><input pInputText [(ngModel)]="editForm.name" name="editName" /></label>
              <label>
                <span>{{ t('insurance.commissions.planDetail.fields.basis') }}</span>
                <p-select
                  [options]="basisOptions"
                  optionValue="value"
                  [(ngModel)]="editForm.basis"
                  name="editBasis"
                  appendTo="body"
                  (ngModelChange)="onEditBasisChanged($event)">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label><span>{{ t('insurance.commissions.planDetail.fields.clawbackMonths') }}</span><p-inputNumber [(ngModel)]="editForm.clawbackMonths" name="editClawbackMonths" [min]="0" [max]="120" /></label>
              <label class="checkbox"><p-checkbox binary="true" [(ngModel)]="editForm.isActive" name="editIsActive" /><span>{{ t('insurance.commissions.planDetail.fields.isActive') }}</span></label>
            </form>
          } @else {
            <div class="section-title">
              <h4>{{ t('insurance.commissions.planDetail.tabs.rules') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.commissions.plans.help.rules.title'"
                [descriptionKey]="'insurance.commissions.plans.help.rules.description'"
                [bulletKeys]="[
                  'insurance.commissions.plans.help.rules.bullets.percent',
                  'insurance.commissions.plans.help.rules.bullets.fixed',
                  'insurance.commissions.plans.help.rules.bullets.tiered'
                ]" />
            </div>
            <div class="rules-block">
              @if (editForm.basis === 'Tiered') {
                <ng-container [ngTemplateOutlet]="tierRulesEditor" [ngTemplateOutletContext]="{ mode: 'edit' }" />
              } @else {
                <ain-structured-json-form [fields]="editRulesFields()" [(payloadJson)]="editForm.rulesJson" />
              }
            </div>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }

      <p-dialog
        [header]="tierDialogIndex() === null ? t('insurance.commissions.planDetail.rules.tierDialog.create') : t('insurance.commissions.planDetail.rules.tierDialog.edit')"
        [modal]="true"
        [(visible)]="tierDialogVisible"
        [style]="{ width: '480px' }">
        <div class="dialog-grid">
          <label>
            <span>{{ t('insurance.commissions.planDetail.rules.fields.upTo') }}</span>
            <p-inputNumber [(ngModel)]="tierDraft.upTo" name="tierUpTo" [min]="0" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.planDetail.rules.fields.percent') }}</span>
            <p-inputNumber [(ngModel)]="tierDraft.percent" name="tierPercent" [min]="0" [max]="100" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="tierDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveTier()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>

    <ng-template #tierRulesEditor let-mode="mode">
      <div class="lines-header">
        <p class="hint">{{ t('insurance.commissions.planDetail.rules.tiersHint') }}</p>
        <button pButton type="button" [label]="t('common.add')" (click)="openTierCreate(mode)"></button>
      </div>
      @if (tiersForMode(mode).length === 0) {
        <p>{{ t('insurance.commissions.planDetail.rules.noTiers') }}</p>
      } @else {
        <p-table [value]="tiersForMode(mode)">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('insurance.commissions.planDetail.rules.fields.upTo') }}</th>
              <th>{{ t('insurance.commissions.planDetail.rules.fields.percent') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row let-i="rowIndex">
            <tr>
              <td>{{ row.upTo ?? t('insurance.commissions.planDetail.rules.unlimited') }}</td>
              <td>{{ row.percent }}%</td>
              <td class="actions-cell">
                <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openTierEdit(mode, i)"></button>
                <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeTier(mode, i)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ng-template>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3, .card h4 { margin: 0; }
    .header { display: grid; gap: 8px; }
    .meta { display: flex; gap: 12px; flex-wrap: wrap; color: #374151; }
    .section-title { display: flex; align-items: center; gap: 8px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .field-label { color: #475569; font-size: 12px; font-weight: 600; }
    .rules-block { display: grid; gap: 10px; }
    .checkbox { display: flex; gap: 8px; align-items: center; }
    .hint { color: #64748b; margin: 0; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .actions-cell { display: flex; gap: 4px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid label { display: grid; gap: 6px; }
    .error { color: #b91c1c; margin: 0; }
    @media (max-width: 900px) { .grid, .dialog-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceCommissionPlanDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly plan = signal<InsuranceCommissionPlanDetail | null>(null);
  readonly isCreateMode = signal(false);
  readonly activeTab = signal<'summary' | 'rules'>('summary');
  readonly tierDialogIndex = signal<number | null>(null);
  readonly tierDialogMode = signal<'create' | 'edit'>('create');
  readonly createTiers = signal<CommissionTierRule[]>([]);
  readonly editTiers = signal<CommissionTierRule[]>([]);

  tierDialogVisible = false;
  tierDraft: CommissionTierRule = { upTo: null, percent: 0 };

  readonly basisOptions = INSURANCE_COMMISSION_BASIS_OPTIONS;

  createRulesFields() {
    return getCommissionPlanRulesFields(this.createForm.basis);
  }

  editRulesFields() {
    return getCommissionPlanRulesFields(this.editForm.basis);
  }

  createForm: CreateInsuranceCommissionPlanPayload = {
    legalEntityId: '',
    code: '',
    name: '',
    basis: 'PercentOfPremium',
    rulesJson: defaultCommissionRulesJson('PercentOfPremium'),
    clawbackMonths: 6,
    isActive: true
  };

  editForm: UpdateInsuranceCommissionPlanPayload = {
    name: '',
    basis: 'PercentOfPremium',
    rulesJson: '{}',
    clawbackMonths: null,
    isActive: true
  };

  ngOnInit(): void {
    const planId = this.route.snapshot.paramMap.get('planId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }
    if (!planId) {
      this.loading.set(false);
      return;
    }

    this.api.getCommissionPlan(planId).subscribe({
      next: item => {
        this.plan.set(item);
        if (item) {
          this.editForm = {
            name: item.name,
            basis: item.basis,
            rulesJson: item.rulesJson,
            clawbackMonths: item.clawbackMonths,
            isActive: item.isActive
          };
          if (item.basis === 'Tiered') {
            this.editTiers.set(parseCommissionTiers(item.rulesJson));
          }
        }
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    if (this.isCreateMode()) {
      return this.t('insurance.commissions.planDetail.createTitle');
    }
    const current = this.plan();
    return current ? `${current.code} - ${current.name}` : this.t('insurance.commissions.planDetail.title');
  }

  basisLabelKey(basis: string): string {
    return commissionBasisLabelKey(basis);
  }

  tiersForMode(mode: 'create' | 'edit'): CommissionTierRule[] {
    return mode === 'create' ? this.createTiers() : this.editTiers();
  }

  onCreateBasisChanged(basis: string): void {
    this.createForm.rulesJson = defaultCommissionRulesJson(basis);
    this.createTiers.set(basis === 'Tiered' ? [] : []);
  }

  onEditBasisChanged(basis: string): void {
    this.editForm.rulesJson = defaultCommissionRulesJson(basis);
    this.editTiers.set(basis === 'Tiered' ? parseCommissionTiers(this.editForm.rulesJson) : []);
  }

  openTierCreate(mode: 'create' | 'edit'): void {
    this.tierDialogMode.set(mode);
    this.tierDialogIndex.set(null);
    this.tierDraft = { upTo: null, percent: 0 };
    this.tierDialogVisible = true;
  }

  openTierEdit(mode: 'create' | 'edit', index: number): void {
    const tiers = mode === 'create' ? this.createTiers() : this.editTiers();
    const current = tiers[index];
    if (!current) {
      return;
    }
    this.tierDialogMode.set(mode);
    this.tierDialogIndex.set(index);
    this.tierDraft = { upTo: current.upTo, percent: current.percent };
    this.tierDialogVisible = true;
  }

  saveTier(): void {
    const mode = this.tierDialogMode();
    const tiers = [...(mode === 'create' ? this.createTiers() : this.editTiers())];
    const index = this.tierDialogIndex();
    const nextTier: CommissionTierRule = {
      upTo: this.tierDraft.upTo,
      percent: this.tierDraft.percent
    };
    if (index === null) {
      tiers.push(nextTier);
    } else {
      tiers[index] = nextTier;
    }
    if (mode === 'create') {
      this.createTiers.set(tiers);
      this.createForm.rulesJson = serializeCommissionTiers(tiers);
    } else {
      this.editTiers.set(tiers);
      this.editForm.rulesJson = serializeCommissionTiers(tiers);
    }
    this.tierDialogVisible = false;
  }

  removeTier(mode: 'create' | 'edit', index: number): void {
    const tiers = [...(mode === 'create' ? this.createTiers() : this.editTiers())];
    tiers.splice(index, 1);
    if (mode === 'create') {
      this.createTiers.set(tiers);
      this.createForm.rulesJson = serializeCommissionTiers(tiers);
    } else {
      this.editTiers.set(tiers);
      this.editForm.rulesJson = serializeCommissionTiers(tiers);
    }
  }

  saveCreate(): void {
    if (this.createForm.basis === 'Tiered') {
      this.createForm.rulesJson = serializeCommissionTiers(this.createTiers());
    }
    this.saving.set(true);
    this.error.set('');
    this.api.createCommissionPlan(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/commission-plans', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveEdit(): void {
    const current = this.plan();
    if (!current) {
      return;
    }
    if (this.editForm.basis === 'Tiered') {
      this.editForm.rulesJson = serializeCommissionTiers(this.editTiers());
    }

    this.saving.set(true);
    this.error.set('');
    this.api.updateCommissionPlan(current.id, this.editForm).subscribe({
      next: item => {
        this.plan.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
